import {
  CmsContentAboutServicesSchema,
  type CmsContentAboutServicesForm,
} from "@/modules/cms/domain/contracts";
import { normalizeAboutServicesContent } from "@/modules/cms/domain/normalize-about-services";
import { AboutServiceItemCard } from "@/modules/cms/presentation/ui/forms/AboutServiceItemCard";
import type { ServiceItemSource } from "@/modules/cms/presentation/ui/forms/ServiceItemSourceSelector";
import { slugify } from "@/shared/lib/slugify";
import { isStockPublicImageKey } from "@/shared/lib/stock-public-image-key";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import {
  Button,
  FormCheckbox,
  FormRoot,
  RHF,
  zodResolver,
} from "@devhop/ui";
import { Database, Inbox, PenLine } from "lucide-react";
import { nanoid } from "nanoid";
import { useCallback, useMemo, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-services";

type AboutServicesFormProps = {
  content: CmsContentAboutServicesForm | null;
  onSubmit: (content: CmsContentAboutServicesForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutServicesForm = {
  items: [],
  isActive: true,
};

function buildInitialSources(
  content: CmsContentAboutServicesForm | null,
): Record<string, ServiceItemSource> {
  const normalized = normalizeAboutServicesContent(content ?? defaultContent);
  const map: Record<string, ServiceItemSource> = {};
  for (const item of normalized.items) {
    map[item.id] = item.stockServiceCode?.trim() ? "stock" : "manual";
  }
  return map;
}

function emptyServiceItem(order: number): CmsContentAboutServicesForm["items"][number] {
  return {
    id: nanoid(),
    slug: `service-${order + 1}`,
    imageKey: "",
    title: {},
    content: {},
    intro: {},
    linkMode: "none",
    productIds: [],
    stockServiceCode: null,
    showStockPrice: false,
    isActive: true,
    order,
  };
}

export function AboutServicesForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutServicesFormProps) {
  const initialSources = useMemo(() => buildInitialSources(content), [content]);

  const methods = RHF.useForm<CmsContentAboutServicesForm>({
    resolver: zodResolver(
      CmsContentAboutServicesSchema,
    ) as Resolver<CmsContentAboutServicesForm>,
    defaultValues: normalizeAboutServicesContent(content ?? defaultContent),
  });
  const { fields, append, remove } = RHF.useFieldArray({
    control: methods.control,
    name: "items",
  });
  const [pendingFiles, setPendingFiles] = useState<Record<string, File>>({});
  const [openServices, setOpenServices] = useState<Record<number, boolean>>({});
  const [itemSources, setItemSources] =
    useState<Record<string, ServiceItemSource>>(initialSources);

  const setPending = useCallback((index: number, file: File | null) => {
    setPendingFiles((prev) => {
      const next = { ...prev };
      if (file) next[index] = file;
      else delete next[index];
      return next;
    });
  }, []);

  const handleSourceChange = useCallback(
    (itemId: string, index: number, source: ServiceItemSource) => {
      setItemSources((prev) => ({ ...prev, [itemId]: source }));
      if (source === "stock") {
        methods.setValue(`items.${index}.linkMode`, "none", { shouldDirty: true });
        methods.setValue(`items.${index}.showcaseKind`, undefined, {
          shouldDirty: true,
        });
      }
      if (source === "manual") {
        methods.setValue(`items.${index}.stockServiceCode`, null, {
          shouldDirty: true,
        });
        methods.setValue(`items.${index}.showStockPrice`, false, {
          shouldDirty: true,
        });
        const currentKey = methods.getValues(`items.${index}.imageKey`);
        if (isStockPublicImageKey(currentKey)) {
          methods.setValue(`items.${index}.imageKey`, "", { shouldDirty: true });
        }
        setPending(index, null);
      }
    },
    [methods, setPending],
  );

  const appendService = useCallback(
    (source: ServiceItemSource) => {
      const nextIndex = fields.length;
      const item = emptyServiceItem(nextIndex);
      append(item);
      setItemSources((prev) => ({ ...prev, [item.id]: source }));
      setOpenServices({ [nextIndex]: true });
    },
    [append, fields.length],
  );

  const handleRemove = useCallback(
    (index: number, itemId: string) => {
      remove(index);
      setItemSources((prev) => {
        const next = { ...prev };
        delete next[itemId];
        return next;
      });
      setOpenServices((prev) => {
        const next: Record<number, boolean> = {};
        for (const [key, value] of Object.entries(prev)) {
          const i = Number(key);
          if (i < index) next[i] = value;
          else if (i > index) next[i - 1] = value;
        }
        return next;
      });
      setPendingFiles((prev) => {
        const next: Record<string, File> = {};
        for (const [key, value] of Object.entries(prev)) {
          const i = Number(key);
          if (i < index) next[i] = value;
          else if (i > index) next[i - 1] = value;
        }
        return next;
      });
    },
    [remove],
  );

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutServicesForm) => {
      const items = (vals.items ?? []).map((item, i) => {
        const f = pendingFiles[i];
        const stockServiceCode = item.stockServiceCode?.trim() || null;
        const fromStock = !!stockServiceCode;
        const imageKey = fromStock || !f ? (item.imageKey ?? "") : "";
        return {
          id: item.id?.trim() || nanoid(),
          slug: item.slug?.trim() || slugify(item.title?.en ?? `service-${i}`),
          imageKey,
          title: item.title ?? {},
          content: item.content ?? {},
          intro: item.intro ?? undefined,
          linkMode: fromStock ? "none" : (item.linkMode ?? "none"),
          showcaseKind:
            !fromStock && item.linkMode === "showcase"
              ? (item.showcaseKind ?? "production")
              : undefined,
          productIds: [],
          stockServiceCode,
          showStockPrice: stockServiceCode
            ? item.showStockPrice !== false
            : false,
          isActive: item.isActive ?? true,
          order: item.order ?? i,
        };
      });
      for (let i = 0; i < items.length; i++) {
        const f = pendingFiles[i];
        const row = items[i];
        if (f && row && !row.stockServiceCode) {
          row.imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, f);
        }
      }
      setPendingFiles({});
      onSubmit({ ...vals, items });
    },
    [onSubmit, pendingFiles],
  );

  const watchedItems = methods.watch("items");
  const activeCount = (watchedItems ?? []).filter(
    (item) => item?.isActive !== false,
  ).length;

  return (
    <FormRoot<CmsContentAboutServicesForm>
      methods={methods as UseFormReturn<CmsContentAboutServicesForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="font-medium text-sm">ລາຍການບໍລິການ</h3>
              <p className="text-muted-foreground text-xs">
                {fields.length === 0
                  ? "ຍັງບໍ່ມີບໍລິການ — ເພີ່ມຈາກ Stock ຫຼື ກຳນົດເອງ"
                  : `${fields.length} ລາຍການ · ${activeCount} ເປີດໃຊ້ງານ`}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendService("stock")}
              >
                <Database className="size-4" />
                ເພີ່ມຈາກ Stock
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => appendService("manual")}
              >
                <PenLine className="size-4" />
                ເພີ່ມແບບ CMS
              </Button>
            </div>
          </div>

          {fields.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed bg-muted/20 px-6 py-12 text-center">
              <Inbox className="size-10 text-muted-foreground/60" />
              <div>
                <p className="font-medium text-sm">ຍັງບໍ່ມີບໍລິການ</p>
                <p className="mt-1 max-w-sm text-muted-foreground text-xs">
                  ເລືອກ &quot;ເພີ່ມຈາກ Stock&quot; ເພື່ອດຶງລາຄາ/spec/ຮູບ
                  ຫຼື &quot;ເພີ່ມແບບ CMS&quot; ສຳລັບບໍລິການກຳນົດເອງ
                </p>
              </div>
              <div className="flex flex-wrap justify-center gap-2">
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => appendService("stock")}
                >
                  <Database className="size-4" />
                  ເພີ່ມຈາກ Stock
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => appendService("manual")}
                >
                  <PenLine className="size-4" />
                  ເພີ່ມແບບ CMS
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {fields.map((field, index) => (
                <AboutServiceItemCard
                  key={field.id}
                  index={index}
                  source={itemSources[field.id] ?? "manual"}
                  isOpen={openServices[index] ?? false}
                  onToggle={() =>
                    setOpenServices((prev) => ({
                      ...prev,
                      [index]: !prev[index],
                    }))
                  }
                  onSourceChange={(next) =>
                    handleSourceChange(field.id, index, next)
                  }
                  onRemove={() => handleRemove(index, field.id)}
                  methods={methods}
                  pendingFile={pendingFiles[index] ?? null}
                  onPendingFileChange={(file) => setPending(index, file)}
                />
              ))}
            </div>
          )}
        </div>

        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານທັງໝວດ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting}>
            {submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
